import { inject, Injectable, InjectionToken } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, Observable, of, retry, switchMap, throwError, timer } from 'rxjs';

import { PersonModel } from '@ninety/mobile/platform/api';
import { AuthFailureCause, isRetryable } from '@ninety/mobile/platform/auth';
import { DatadogLogsService } from '@ninety/mobile/platform/services';

import { appActions } from '../../app/app.actions';
import { AuthFailureError, classifyFailure } from '../session/auth-failure';
import { SessionActions } from '../session/session.actions';
import { SessionSelectors } from '../session/session.selectors';

import { CurrentPersonStateActions } from './current-person-state.actions';
import { PersonApiService } from './person-api.service';

/** Total attempts, first included. Three failures is a server that is not about to answer. */
export const PROFILE_FETCH_ATTEMPTS = 3;

/**
 * How the profile fetch backs off between attempts.
 *
 * Injectable for the same reason `AUTH_DELAY` is: the retry policy is a rule worth asserting on, and
 * asserting on it should not cost a spec three seconds or a set of fake timers.
 */
export const PROFILE_RETRY_DELAY = new InjectionToken<(attempt: number) => Observable<unknown>>('ProfileRetryDelay', {
  providedIn: 'root',
  factory: () => attempt => timer(attempt * 1_000),
});

/**
 * What a failed profile fetch means, in the machine's vocabulary.
 *
 * `classifyFailure` throws on an error it does not recognise, which is right where it is used — a
 * Command handler reports that as a bug and leaves the state alone. Here there is no such seam: this
 * is the last stop, and an unhandled throw would kill the effect stream and take every later profile
 * failure with it. So an unrecognisable error is treated as the retryable one, which is the reading
 * that discards nothing.
 */
function profileFailureCause(error: unknown): AuthFailureCause {
  try {
    return classifyFailure(error);
  } catch {
    return { kind: 'network' };
  }
}

@Injectable()
export class CurrentPersonEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private personApi = inject(PersonApiService);
  private logs = inject(DatadogLogsService);
  private readonly retryDelay = inject(PROFILE_RETRY_DELAY);

  /**
   * Who to fetch a profile for, read off the Access Token.
   *
   * `initGlobalState` is dispatched from the machine's `notifyAppSignedIn` Command, and
   * `stateAdvanced` precedes every Command — so by the time this runs the slice already holds the
   * tokens that were just minted, and `concatLatestFrom` cannot see the previous ones.
   *
   * An unreadable token is a genuine dead end: there is no `personId` to fetch and no reason to
   * expect the next attempt to differ, so it ends the session. Note that this is the only kind of
   * failure that does — a network failure fetching the person is handled below, and does not.
   */
  initGlobalState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.initGlobalState),
      concatLatestFrom(() => this.store.select(SessionSelectors.accessTokenClaims)),
      map(([, claims]) => {
        if (!claims?.personId) {
          this.logs.error('No readable personId on the Access Token for CurrentPersonEffects');
          // `sessionUnusable`, like the sibling below, and for the reason that one states:
          // `userRequested` is the one Sign-out reason that is never a defect, so reporting this as
          // one files an impossible state as something somebody asked for. `unexpectedFailure`
          // because that is exactly what this is — a token the machine had just minted, unreadable.
          // Neither reason clears the Vault, so nothing here costs a stored sign-in.
          return SessionActions.eventReceived({
            event: { type: 'sessionUnusable', cause: { kind: 'unexpectedFailure' } },
          });
        }

        return CurrentPersonStateActions.getLoginPerson({ _id: claims.personId });
      })
    )
  );

  /**
   * Fetch the profile, and keep trying while trying is the right thing to do.
   *
   * A 401 never reaches the retry: the interceptor refreshes and replays it before this sees a
   * failure. So anything here is either the server not answering — offline, a timeout, a 5xx, which
   * is worth another attempt — or a refusal, which another attempt will not change.
   */
  getLoginPerson$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentPersonStateActions.getLoginPerson),
      switchMap(() =>
        this.personApi.getCurrentPerson().pipe(
          map(person => {
            if (!person) throw new AuthFailureError({ kind: 'unexpectedFailure' }, 'Empty response for current person');
            return person;
          }),
          retry({
            count: PROFILE_FETCH_ATTEMPTS - 1,
            delay: (error: unknown, attempt) =>
              isRetryable(profileFailureCause(error)) ? this.retryDelay(attempt) : throwError(() => error),
          }),
          map((person: PersonModel) => CurrentPersonStateActions.getLoginPersonSuccess({ person })),
          catchError((error: unknown) => {
            this.logs.error('Failed to get login person', undefined, error as Error);
            return of(CurrentPersonStateActions.getLoginPersonFailed({ error }));
          })
        )
      )
    )
  );

  /**
   * A profile fetch that has run out of attempts ends the session — and says so.
   *
   * Ending it at all is a judgement rather than an obligation: the credentials are fine, and what
   * failed is a resource call. But nothing downstream can run without a person — `AppSelectors.appReady`
   * never turns true, so `AppReadyGuardFn` times out and routes to the sign-in screen anyway. Ending
   * it deliberately is the same destination reached honestly, and it is the difference between a
   * sign-in screen and a spinner nobody can leave.
   *
   * `sessionUnusable` destroys neither the Durable Root nor the Cognito session, so a server outage
   * does not cost someone their password and the next launch restores silently. It also reports
   * honestly: `userRequested` is the one Sign-out reason that is not a defect, and this is not that.
   */
  getLoginPersonFailed_endSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentPersonStateActions.getLoginPersonFailed),
      map(({ error }) =>
        SessionActions.eventReceived({ event: { type: 'sessionUnusable', cause: profileFailureCause(error) } })
      )
    )
  );
}
